import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { sessionManager } from '../config/sessionManager';
import { PermissionLevel } from '../config/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireEmployee?: boolean; // Employee-only access
  requireAdminOrEmployee?: boolean; // Admin or employee access
  requireAdminOrCoach?: boolean; // Admin or coach access
  requireAdminOrCoachOrEmployee?: boolean; // Admin, coach, stat manager, or employee access
  requireAuth?: boolean;
  requirePermission?: string; // Check specific permission (e.g., 'users', 'players')
  minimumPermissionLevel?: PermissionLevel; // Minimum permission level required (default: VIEW)
}

/**
 * Finds the first accessible route based on user permissions
 * Routes are checked in priority order
 * @returns The path to the first accessible route, or '/games' as fallback
 */
export const getFirstAccessibleRoute = (): string => {
  const isEmployee = sessionManager.isEmployee();

  // Priority order: Check routes in order of preference

  // 1. Dashboard - employees only (high priority for employees)
  if (isEmployee) {
    return '/dashboard';
  }

  // 2. Players - requires players permission (first step for new coaches)
  if (sessionManager.hasPermission('players', PermissionLevel.VIEW)) {
    return '/players';
  }

  // 3. Opponents - requires opponents permission
  if (sessionManager.hasPermission('opponents', PermissionLevel.VIEW)) {
    return '/opponents';
  }

  // 4. Seasons - requires seasons permission
  if (sessionManager.hasPermission('seasons', PermissionLevel.VIEW)) {
    return '/seasons';
  }

  // 5. Users - requires users permission
  if (sessionManager.hasPermission('users', PermissionLevel.VIEW)) {
    return '/users';
  }

  // 6. Games - accessible to all authenticated users (no permission check)
  // This is the fallback route that should always be accessible
  return '/games';

  // Note: The following routes are also accessible but lower priority:
  // - /profile - accessible to all authenticated users
  // - /setup/stat-takers - accessible to all authenticated users
  // - /account - accessible to all authenticated users
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireEmployee = false,
  requireAdminOrEmployee = false,
  requireAdminOrCoach = false,
  requireAdminOrCoachOrEmployee = false,
  requireAuth = true,
  requirePermission,
  minimumPermissionLevel = PermissionLevel.VIEW,
}) => {
  const location = useLocation();
  const isAuthenticated = sessionManager.isAuthenticated();
  const isAdmin = sessionManager.isAdmin();
  const isCoach = sessionManager.isCoach();
  const isEmployee = sessionManager.isEmployee();
  const isStatManager = sessionManager.isStatManager();

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get the first accessible route for redirects
  const accessibleRoute = getFirstAccessibleRoute();

  // Check permission-based access
  if (requirePermission) {
    const hasRequiredPermission = sessionManager.hasPermission(requirePermission, minimumPermissionLevel);
    if (!hasRequiredPermission) {
      return <Navigate to={accessibleRoute} replace />;
    }
  }

  // Check employee-only access
  if (requireEmployee && !isEmployee) {
    return <Navigate to={accessibleRoute} replace />;
  }

  // Check admin or employee privileges
  if (requireAdminOrEmployee && !isAdmin && !isEmployee) {
    return <Navigate to={accessibleRoute} replace />;
  }

  // Check admin privileges
  if (requireAdmin && !isAdmin) {
    return <Navigate to={accessibleRoute} replace />;
  }

  // Check admin or coach privileges
  if (requireAdminOrCoach && !isAdmin && !isCoach) {
    return <Navigate to={accessibleRoute} replace />;
  }

  // Check admin, coach, stat manager, or employee privileges
  if (requireAdminOrCoachOrEmployee && !isAdmin && !isCoach && !isStatManager && !isEmployee) {
    return <Navigate to={accessibleRoute} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
